class PortfolioGallery extends HTMLElement {
  static CATEGORY_ORDER = ['residential', 'commercial', 'installation'];

  static CATEGORY_LABELS = {
    residential: 'Residential',
    commercial: 'Commercial',
    installation: 'Installation',
  };

  static getCategorySortIndex(category) {
    const index = PortfolioGallery.CATEGORY_ORDER.indexOf(category);
    return index === -1 ? PortfolioGallery.CATEGORY_ORDER.length : index;
  }

  static getCategoryLabel(key, fallback = '') {
    return PortfolioGallery.CATEGORY_LABELS[key] || fallback || key;
  }

  static getCategoryLabels() {
    const categories = new Map();
    document.querySelectorAll('[data-portfolio-category]').forEach((el) => {
      const key = el.dataset.category;
      if (!key || categories.has(key)) return;
      const fallback = el.querySelector('.portfolio-category__title')?.textContent?.trim();
      categories.set(key, PortfolioGallery.getCategoryLabel(key, fallback));
    });
    if (categories.size) return categories;

    PortfolioGallery.getAllProjects().forEach((project) => {
      const key = project.dataset.category;
      if (!key || categories.has(key)) return;
      const fallback = project.querySelector('.portfolio-project__category')?.textContent?.trim();
      categories.set(key, PortfolioGallery.getCategoryLabel(key, fallback));
    });
    return categories;
  }

  static sortAllProjects() {
    const sortCategories = (host) => {
      const categories = Array.from(host.querySelectorAll(':scope > [data-portfolio-category]'));
      if (categories.length > 1) {
        categories.sort((a, b) => {
          const diff =
            PortfolioGallery.getCategorySortIndex(a.dataset.category) -
            PortfolioGallery.getCategorySortIndex(b.dataset.category);
          return diff !== 0 ? diff : categories.indexOf(a) - categories.indexOf(b);
        });
        categories.forEach((category) => host.appendChild(category));
        return;
      }

      const projects = Array.from(host.querySelectorAll(':scope > [data-portfolio-project-carousel]'));
      if (projects.length <= 1) return;

      projects.sort((a, b) => {
        const diff =
          PortfolioGallery.getCategorySortIndex(a.dataset.category) -
          PortfolioGallery.getCategorySortIndex(b.dataset.category);
        return diff !== 0 ? diff : projects.indexOf(a) - projects.indexOf(b);
      });
      projects.forEach((project) => host.appendChild(project));
    };

    document.querySelectorAll('portfolio-gallery').forEach((gallery) => {
      const host = gallery.querySelector('.portfolio-gallery__projects');
      if (host) sortCategories(host);
    });

    const galleries = Array.from(document.querySelectorAll('portfolio-gallery'));
    if (galleries.length <= 1) return;

    const getSectionKey = (gallery) => {
      const projects = gallery.querySelectorAll('[data-portfolio-project-carousel]');
      if (!projects.length) return PortfolioGallery.CATEGORY_ORDER.length;
      return Math.min(
        ...Array.from(projects, (project) =>
          PortfolioGallery.getCategorySortIndex(project.dataset.category)
        )
      );
    };

    const sectionGroups = new Map();
    galleries.forEach((gallery) => {
      const section = gallery.closest('.shopify-section') || gallery;
      if (!sectionGroups.has(section)) {
        sectionGroups.set(section, { section, key: getSectionKey(gallery) });
      } else {
        sectionGroups.get(section).key = Math.min(
          sectionGroups.get(section).key,
          getSectionKey(gallery)
        );
      }
    });

    const sortedSections = [...sectionGroups.values()].sort((a, b) => a.key - b.key);
    const parent = sortedSections[0]?.section.parentElement;
    if (!parent) return;

    sortedSections.forEach(({ section }) => parent.appendChild(section));
  }

  static getSliderVisibleCount(gallery) {
    if (!gallery) return 2;

    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const raw = mobile ? gallery.dataset.sliderVisibleMobile : gallery.dataset.sliderVisible;
    const value = Number.parseInt(raw, 10);
    return Number.isFinite(value) && value > 0 ? value : mobile ? 1 : 2;
  }

  static updateSliderVisibleVars(gallery) {
    if (!gallery) return;
    gallery.style.setProperty('--slider-visible', PortfolioGallery.getSliderVisibleCount(gallery));
  }

  static updateSliderTrackPosition(track, slides, startIndex, visibleCount) {
    if (!track || !slides.length) return;

    const gap = Number.parseFloat(getComputedStyle(track).gap) || 12;
    const viewport = track.parentElement;
    const slideList = Array.from(slides);

    slideList.forEach((slide, index) => {
      const inView = index >= startIndex && index < startIndex + visibleCount;
      slide.classList.toggle('is-active', inView);
    });

    if (slides.length <= visibleCount) {
      const viewportWidth = viewport?.offsetWidth || 0;
      const offset = Math.max(0, (viewportWidth - track.scrollWidth) / 2);
      track.style.transform = `translate3d(${offset}px, 0, 0)`;
      return;
    }

    let offset = 0;
    for (let index = 0; index < startIndex; index += 1) {
      offset += slideList[index].offsetWidth + gap;
    }

    track.style.transform = `translate3d(-${offset}px, 0, 0)`;
  }

  static bindSliderTrackMedia(track, slides, updatePosition) {
    if (!track || track.dataset.portfolioTrackBound === 'true') return;
    track.dataset.portfolioTrackBound = 'true';

    Array.from(slides).forEach((slide) => {
      slide.querySelectorAll('img, video').forEach((media) => {
        if (media.complete || media.readyState >= 2) return;
        media.addEventListener('load', updatePosition, { once: true });
        media.addEventListener('loadeddata', updatePosition, { once: true });
      });
    });
  }

  static bindMainCarouselSwipe(project, getCurrent, getLength, goTo) {
    if (project.closest('.portfolio-category__track')) return;

    const viewport = project.querySelector('.portfolio-project__main-viewport');
    if (!viewport || viewport.dataset.portfolioSwipeBound === 'true') return;
    viewport.dataset.portfolioSwipeBound = 'true';

    let startX = 0;
    let startY = 0;
    let axis = null;

    const resetGesture = () => {
      axis = null;
    };

    viewport.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        startX = event.clientX;
        startY = event.clientY;
        axis = null;
        viewport.setPointerCapture?.(event.pointerId);
      },
      { passive: true }
    );

    viewport.addEventListener(
      'pointermove',
      (event) => {
        if (axis === null) {
          const dx = Math.abs(event.clientX - startX);
          const dy = Math.abs(event.clientY - startY);
          if (dx > 10 || dy > 10) axis = dx > dy ? 'x' : 'y';
        }
        if (axis === 'x') event.preventDefault();
      },
      { passive: false }
    );

    const finishSwipe = (event) => {
      viewport.releasePointerCapture?.(event.pointerId);
      if (axis !== 'x') {
        resetGesture();
        return;
      }

      const delta = event.clientX - startX;
      resetGesture();
      if (Math.abs(delta) < 36) return;

      event.stopPropagation();
      const current = getCurrent();
      const length = getLength();
      if (length <= 1) return;

      if (delta < 0) {
        goTo(current >= length - 1 ? 0 : current + 1);
      } else {
        goTo(current <= 0 ? length - 1 : current - 1);
      }
    };

    viewport.addEventListener('pointerup', finishSwipe);
    viewport.addEventListener('pointercancel', finishSwipe);
  }

  static collectAllGalleryMainItems(gallery) {
    const root = gallery || document.querySelector('portfolio-gallery');
    if (!root) return [];

    const items = [];
    root.querySelectorAll('[data-portfolio-category]:not(.is-hidden)').forEach((category) => {
      category.querySelectorAll('[data-portfolio-wallpaper-set]:not(.is-hidden)').forEach((set) => {
        set.querySelectorAll('[data-portfolio-slide]').forEach((slide) => {
          const mainItem = slide.querySelector('[data-portfolio-item].portfolio-gallery__item--main');
          if (mainItem) items.push(mainItem);
        });
      });
    });
    return items;
  }

  static getAllProjects() {
    return document.querySelectorAll('portfolio-gallery [data-portfolio-wallpaper-set]');
  }

  static getAllItems() {
    return document.querySelectorAll('portfolio-gallery [data-portfolio-item]');
  }

  static getFilterController() {
    return document.querySelector('portfolio-gallery[data-portfolio-filter-controller]');
  }

  static collectVisibleItems() {
    return Array.from(PortfolioGallery.getAllItems()).filter((item) => {
      if (item.classList.contains('is-hidden')) return false;

      const category = item.closest('[data-portfolio-category]');
      if (category?.classList.contains('is-hidden')) return false;

      const set = item.closest('[data-portfolio-wallpaper-set]');
      if (category && set) {
        const track = category.querySelector('[data-portfolio-category-track]');
        const isStacked =
          track &&
          (track.classList.contains('portfolio-category__track--strip') ||
            !track.classList.contains('portfolio-category__track--carousel'));
        if (!isStacked) {
          const activeSet = category.querySelector('[data-portfolio-wallpaper-set].is-active-set');
          if (activeSet && set !== activeSet) return false;
        }
      }

      const wallpaperSet = item.closest('[data-portfolio-wallpaper-set]');
      if (wallpaperSet?.classList.contains('is-hidden')) return false;

      const row = item.closest('[data-portfolio-project-carousel]');
      if (row?.classList.contains('is-hidden')) return false;
      const slide = item.closest('[data-portfolio-slide]');
      return !slide || slide.classList.contains('is-active');
    });
  }

  static syncGallerySectionVisibility() {
    document.querySelectorAll('portfolio-gallery').forEach((gallery) => {
      if (gallery.hasAttribute('data-portfolio-filter-controller')) return;

      const visibleCategories = gallery.querySelectorAll(
        '[data-portfolio-category]:not(.is-hidden)'
      );
      const visibleProjects = gallery.querySelectorAll(
        '[data-portfolio-wallpaper-set]:not(.is-hidden)'
      );
      const hasContent = visibleCategories.length || visibleProjects.length;
      gallery.classList.toggle('is-filter-hidden', !hasContent);
    });
  }

  connectedCallback() {
    this.projects = this.querySelectorAll('[data-portfolio-project-carousel]');
    this.items = this.querySelectorAll('[data-portfolio-item]');
    this.lightbox = this.querySelector('[data-portfolio-lightbox]');
    this.lightboxMedia = this.querySelector('[data-portfolio-lightbox-media]');
    this.lightboxCaption = this.querySelector('[data-portfolio-lightbox-caption]');
    this.lightboxCounter = this.querySelector('[data-portfolio-lightbox-counter]');
    this.lightboxClose = this.querySelector('[data-portfolio-lightbox-close]');
    this.lightboxPrev = this.querySelector('[data-portfolio-lightbox-prev]');
    this.lightboxNext = this.querySelector('[data-portfolio-lightbox-next]');
    this.featuredTrack = this.querySelector('[data-portfolio-featured-track]');
    this.featuredPrev = this.querySelector('[data-portfolio-featured-prev]');
    this.featuredNext = this.querySelector('[data-portfolio-featured-next]');
    this.filtersHost = this.querySelector('.portfolio-gallery__filters');

    this.visibleItems = [];
    this.currentIndex = 0;
    this.projectTimers = [];
    this.featuredIndex = 0;
    this.isFilterController = Boolean(
      this.filtersHost && !PortfolioGallery.getFilterController()
    );
    this.handleResize = null;
    this._resizeTimer = 0;
    this.projectCarouselUpdates = [];

    PortfolioGallery.updateSliderVisibleVars(this);

    if (this.isFilterController) {
      this.setAttribute('data-portfolio-filter-controller', '');
      PortfolioGallery.sortAllProjects();
      this.projects = this.querySelectorAll('[data-portfolio-project-carousel]');
    }

    if (this.isFilterController) {
      this.buildCategoryFilters();
      this.buildCategoryHeadings();
      this.bindFilters();
    }

    this.buildFeaturedSlides();
    this.bindFeaturedCarousel();
    this.bindItems();
    this.bindLightbox();
    this.bindProjectCarousels();
    this.bindCategorySetCarousels();
    this.refreshVisibleItems();

    if (this.isFilterController) {
      PortfolioGallery.syncGallerySectionVisibility();
    }

    this.handleResize = () => {
      window.clearTimeout(this._resizeTimer);
      this._resizeTimer = window.setTimeout(() => {
        PortfolioGallery.updateSliderVisibleVars(this);
        this.projectCarouselUpdates.forEach((update) => update());
        this.updateFeaturedCarousel?.();
      }, 120);
    };
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    this.projectTimers.forEach((timer) => window.clearInterval(timer));
    this.projectTimers = [];
    window.clearTimeout(this._resizeTimer);
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
    if (this.handleLightboxKeydown) {
      document.removeEventListener('keydown', this.handleLightboxKeydown);
    }
  }

  buildCategoryFilters() {
    if (!this.filtersHost) return;

    const categories = PortfolioGallery.getCategoryLabels();
    const sortedKeys = [...categories.keys()].sort((a, b) => {
      const indexA = PortfolioGallery.getCategorySortIndex(a);
      const indexB = PortfolioGallery.getCategorySortIndex(b);
      if (indexA === indexB) return a.localeCompare(b);
      return indexA - indexB;
    });

    sortedKeys.forEach((key) => {
      const existing = this.filtersHost.querySelector(`[data-portfolio-filter="${key}"]`);
      if (existing) {
        existing.textContent = categories.get(key);
        return;
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'portfolio-gallery__filter';
      button.dataset.portfolioFilter = key;
      button.textContent = categories.get(key);
      this.filtersHost.appendChild(button);
    });
  }

  buildCategoryHeadings() {
    document.querySelectorAll('[data-portfolio-category-heading]').forEach((heading) => {
      heading.remove();
    });

    if (document.querySelector('[data-portfolio-category]')) {
      this.syncCategoryHeadings(
        this.filtersHost?.querySelector('[data-portfolio-filter].is-active')?.dataset.portfolioFilter || 'all'
      );
      return;
    }

    const categories = PortfolioGallery.getCategoryLabels();

    document.querySelectorAll('.portfolio-gallery__projects').forEach((host) => {
      const projects = Array.from(host.querySelectorAll(':scope > [data-portfolio-project-carousel]'));
      let lastCategory = null;

      projects.forEach((project) => {
        const category = project.dataset.category;
        if (!category || category === lastCategory) return;

        lastCategory = category;
        const heading = document.createElement('h2');
        heading.className = 'portfolio-gallery__category-heading';
        heading.dataset.portfolioCategoryHeading = category;
        heading.textContent = categories.get(category) || PortfolioGallery.getCategoryLabel(category);
        host.insertBefore(heading, project);
      });
    });

    this.syncCategoryHeadings(
      this.filtersHost?.querySelector('[data-portfolio-filter].is-active')?.dataset.portfolioFilter || 'all'
    );
  }

  syncCategoryHeadings(activeFilter = 'all') {
    document.querySelectorAll('[data-portfolio-category-heading]').forEach((heading) => {
      const category = heading.dataset.portfolioCategoryHeading;
      const categoryProjects = Array.from(PortfolioGallery.getAllProjects()).filter(
        (project) => project.dataset.category === category
      );
      const hasVisible = categoryProjects.some((project) => !project.classList.contains('is-hidden'));
      const matchesFilter = activeFilter === 'all' || activeFilter === category;
      heading.classList.toggle('is-hidden', !hasVisible || !matchesFilter);
    });
  }

  buildFeaturedSlides() {
    if (!this.featuredTrack) return;

    const featuredProjects = Array.from(this.projects).filter(
      (project) => project.dataset.featured === 'true'
    );

    featuredProjects.forEach((project, index) => {
      const sourceItem = project.querySelector('[data-portfolio-slide] [data-portfolio-item]');
      const sourceMedia = project.querySelector('[data-portfolio-slide] .portfolio-gallery__media');
      if (!sourceItem || !sourceMedia) return;

      const slide = document.createElement('article');
      slide.className = 'portfolio-gallery__featured-slide';
      slide.dataset.portfolioFeaturedSlide = '';
      if (index === 0) slide.classList.add('is-active');

      const mediaWrap = document.createElement('div');
      mediaWrap.className = 'portfolio-gallery__media portfolio-gallery__media--featured';
      mediaWrap.innerHTML = sourceMedia.innerHTML;

      slide.appendChild(mediaWrap);
      this.featuredTrack.appendChild(slide);
    });

    const featuredSection = this.querySelector('.portfolio-gallery__featured');
    if (featuredSection && !this.featuredTrack.children.length) {
      featuredSection.hidden = true;
    }
  }

  bindFilters() {
    if (!this.filtersHost) return;

    this.filtersHost.addEventListener('click', (event) => {
      const filter = event.target.closest('[data-portfolio-filter]');
      if (!filter) return;

      const category = filter.dataset.portfolioFilter;
      if (filter.classList.contains('is-active')) return;

      this.filtersHost.querySelectorAll('[data-portfolio-filter]').forEach((btn) => {
        btn.classList.remove('is-active');
      });
      filter.classList.add('is-active');

      document.querySelectorAll('[data-portfolio-category]').forEach((block) => {
        const match = category === 'all' || block.dataset.category === category;
        block.classList.toggle('is-hidden', !match);
      });

      PortfolioGallery.getAllProjects().forEach((project) => {
        const match = category === 'all' || project.dataset.category === category;
        project.classList.toggle('is-hidden', !match);
      });

      PortfolioGallery.syncGallerySectionVisibility();

      const controller = PortfolioGallery.getFilterController();
      controller?.syncCategoryHeadings(category);

      if (category !== 'all') {
        const target = document.querySelector(
          `[data-portfolio-category][data-category="${category}"]`
        );
        if (target) {
          const top = target.getBoundingClientRect().top + window.scrollY - 24;
          window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
        }
      }

      document.querySelectorAll('portfolio-gallery').forEach((gallery) => {
        gallery.refreshVisibleItems?.();
      });
    });
  }

  bindItems() {
    this.querySelectorAll('[data-portfolio-item].portfolio-gallery__item--main').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.stopPropagation();
        const controller = PortfolioGallery.getFilterController() || this;
        controller.openLightboxFromItem(item);
      });

      item.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          item.click();
        }
      });
    });
  }

  bindLightbox() {
    if (!this.lightbox) return;

    this.lightboxClose?.addEventListener('click', () => this.closeLightbox());
    this.lightboxPrev?.addEventListener('click', () => this.stepLightbox(-1));
    this.lightboxNext?.addEventListener('click', () => this.stepLightbox(1));

    this.lightbox.addEventListener('click', (event) => {
      if (event.target === this.lightbox) this.closeLightbox();
    });

    if (this.lightboxMedia) {
      let touchStartX = 0;
      this.lightboxMedia.addEventListener(
        'touchstart',
        (event) => {
          touchStartX = event.changedTouches[0]?.clientX || 0;
        },
        { passive: true }
      );
      this.lightboxMedia.addEventListener(
        'touchend',
        (event) => {
          const touchEndX = event.changedTouches[0]?.clientX || 0;
          const delta = touchEndX - touchStartX;
          if (Math.abs(delta) < 48) return;
          this.stepLightbox(delta < 0 ? 1 : -1);
        },
        { passive: true }
      );
    }

    if (this.isFilterController) {
      this.handleLightboxKeydown = (event) => {
        const controller = PortfolioGallery.getFilterController();
        const lightbox = controller?.querySelector('[data-portfolio-lightbox]');
        if (!lightbox?.classList.contains('is-open')) return;
        if (event.key === 'Escape') controller.closeLightbox();
        if (event.key === 'ArrowLeft') controller.stepLightbox(-1);
        if (event.key === 'ArrowRight') controller.stepLightbox(1);
      };
      document.addEventListener('keydown', this.handleLightboxKeydown);
    }
  }

  static layoutCategoryStripRows(track) {
    if (!track || track.dataset.portfolioRowsSplit === 'true') return;

    if (track.querySelector('[data-portfolio-category-row]')) {
      track.dataset.portfolioRowsSplit = 'true';
      return;
    }

    const projects = Array.from(track.querySelectorAll(':scope > [data-portfolio-wallpaper-set]'));
    if (projects.length <= 1) return;

    const rowTop = document.createElement('div');
    rowTop.className = 'portfolio-category__row portfolio-category__row--top';
    rowTop.dataset.portfolioCategoryRow = '';

    const rowBottom = document.createElement('div');
    rowBottom.className = 'portfolio-category__row portfolio-category__row--bottom';
    rowBottom.dataset.portfolioCategoryRow = '';

    projects.forEach((project, index) => {
      if (index % 2 === 0) rowTop.appendChild(project);
      else rowBottom.appendChild(project);
    });

    track.replaceChildren(rowTop, rowBottom);
    track.dataset.portfolioRowsSplit = 'true';
  }

  bindCategorySetCarousels() {
    document.querySelectorAll('[data-portfolio-category]').forEach((category) => {
      const wrap = category.querySelector('.portfolio-category__track-wrap');
      const track = category.querySelector('[data-portfolio-category-track]');
      if (!wrap || !track) return;

      PortfolioGallery.layoutCategoryStripRows(track);

      const sets = Array.from(track.querySelectorAll('[data-portfolio-wallpaper-set]'));
      const prevBtn = category.querySelector('[data-portfolio-set-prev]');
      const nextBtn = category.querySelector('[data-portfolio-set-next]');

      if (!sets.length) return;

      track.classList.add('portfolio-category__track--strip');
      track.style.transform = '';
      sets.forEach((set) => set.classList.add('is-active-set'));

      const updateNav = () => {
        const canScroll = wrap.scrollWidth > wrap.clientWidth + 4;
        category.classList.toggle('has-strip-left', wrap.scrollLeft > 4);
        category.classList.toggle('has-strip-right', canScroll && wrap.scrollLeft + wrap.clientWidth < wrap.scrollWidth - 4);

        if (!canScroll && sets.length <= 1) {
          prevBtn?.setAttribute('hidden', '');
          nextBtn?.setAttribute('hidden', '');
          return;
        }

        prevBtn?.removeAttribute('hidden');
        nextBtn?.removeAttribute('hidden');
      };

      const scrollStrip = (direction) => {
        const maxScroll = Math.max(0, wrap.scrollWidth - wrap.clientWidth);
        const page = Math.max(wrap.clientWidth * 0.88, 320);
        let next = wrap.scrollLeft + direction * page;

        if (direction > 0 && next >= maxScroll - 2) {
          next = maxScroll;
        } else if (direction < 0 && next <= 2) {
          next = 0;
        }

        wrap.scrollTo({ left: Math.max(0, Math.min(next, maxScroll)), behavior: 'auto' });
      };

      if (!category.dataset.portfolioStripBound) {
        category.dataset.portfolioStripBound = 'true';

        wrap.setAttribute('role', 'region');
        wrap.setAttribute('aria-label', 'Scroll wallpaper projects horizontally');

        prevBtn?.addEventListener('click', (event) => {
          event.stopPropagation();
          scrollStrip(-1);
        });
        nextBtn?.addEventListener('click', (event) => {
          event.stopPropagation();
          scrollStrip(1);
        });
        wrap.addEventListener('scroll', updateNav, { passive: true });

        if (typeof ResizeObserver !== 'undefined') {
          const observer = new ResizeObserver(() => updateNav());
          observer.observe(wrap);
          observer.observe(track);
          sets.forEach((set) => observer.observe(set));
        }

        window.addEventListener('resize', updateNav);

        sets.forEach((set) => {
          set.querySelectorAll('img, video').forEach((media) => {
            if (media.complete || media.readyState >= 2) return;
            media.addEventListener('load', updateNav, { once: true });
            media.addEventListener('loadeddata', updateNav, { once: true });
          });
        });
      }

      updateNav();
      window.requestAnimationFrame(updateNav);
      window.setTimeout(updateNav, 350);

      document.querySelectorAll('portfolio-gallery').forEach((gallery) => {
        gallery.refreshVisibleItems?.();
      });
    });
  }

  bindFeaturedCarousel() {
    if (!this.featuredTrack) return;

    const getSlides = () => this.featuredTrack.querySelectorAll('[data-portfolio-featured-slide]');

    this.updateFeaturedCarousel = () => {
      const slides = getSlides();
      const visibleCount = Math.min(PortfolioGallery.getSliderVisibleCount(this), slides.length || 1);
      const maxStart = Math.max(0, slides.length - visibleCount);

      this.featuredIndex = Math.max(0, Math.min(this.featuredIndex, maxStart));

      if (slides.length <= visibleCount) {
        this.featuredPrev?.setAttribute('hidden', '');
        this.featuredNext?.setAttribute('hidden', '');
      } else {
        this.featuredPrev?.removeAttribute('hidden');
        this.featuredNext?.removeAttribute('hidden');
      }

      PortfolioGallery.updateSliderTrackPosition(
        this.featuredTrack,
        slides,
        this.featuredIndex,
        visibleCount
      );
    };

    PortfolioGallery.bindSliderTrackMedia(this.featuredTrack, getSlides(), () => {
      requestAnimationFrame(() => this.updateFeaturedCarousel?.());
    });

    this.featuredPrev?.addEventListener('click', (event) => {
      event.stopPropagation();
      const slides = getSlides();
      const visibleCount = Math.min(PortfolioGallery.getSliderVisibleCount(this), slides.length || 1);
      const maxStart = Math.max(0, slides.length - visibleCount);
      this.featuredIndex = this.featuredIndex <= 0 ? maxStart : this.featuredIndex - 1;
      this.updateFeaturedCarousel();
    });

    this.featuredNext?.addEventListener('click', (event) => {
      event.stopPropagation();
      const slides = getSlides();
      const visibleCount = Math.min(PortfolioGallery.getSliderVisibleCount(this), slides.length || 1);
      const maxStart = Math.max(0, slides.length - visibleCount);
      this.featuredIndex = this.featuredIndex >= maxStart ? 0 : this.featuredIndex + 1;
      this.updateFeaturedCarousel();
    });

    this.updateFeaturedCarousel();
  }

  bindProjectCarousels() {
    this.querySelectorAll('[data-portfolio-project-carousel]').forEach((project) => {
      this.initProjectCarousel(project);
    });
  }

  initProjectCarousel(project) {
    const mainSlides = project.querySelectorAll('[data-portfolio-slide]');
    const thumbs = project.querySelectorAll('[data-portfolio-thumb]');
    const mainTrack = project.querySelector('[data-portfolio-main-track]');

    if (!mainSlides.length || !mainTrack || project.dataset.portfolioCarouselBound === 'true') return;
    project.dataset.portfolioCarouselBound = 'true';

    mainSlides.forEach((slide, index) => {
      slide.dataset.slideIndex = String(index);
      slide.querySelectorAll('[data-portfolio-item]').forEach((item) => {
        item.dataset.slideIndex = String(index);
      });
    });

    thumbs.forEach((thumb, index) => {
      thumb.dataset.slideIndex = String(index);
    });

    let current = 0;

    const pauseVideos = () => {
      mainSlides.forEach((slide, index) => {
        slide.querySelectorAll('video').forEach((video) => {
          if (index === current) return;
          video.pause();
          video.currentTime = 0;
        });
      });
    };

    const scrollActiveThumb = () => {
      const activeThumb = thumbs[current];
      const thumbTrack = project.querySelector('[data-portfolio-thumb-track]');
      if (!activeThumb || !thumbTrack) return;

      const trackTop = thumbTrack.scrollTop;
      const thumbTop = activeThumb.offsetTop;
      const thumbBottom = thumbTop + activeThumb.offsetHeight;
      const viewBottom = trackTop + thumbTrack.clientHeight;

      if (thumbTop < trackTop) {
        thumbTrack.scrollTop = thumbTop;
      } else if (thumbBottom > viewBottom) {
        thumbTrack.scrollTop = thumbBottom - thumbTrack.clientHeight;
      }
    };

    const isCategoryStrip = Boolean(project.closest('.portfolio-category__track'));
    const thumbOffset = Number.parseInt(project.dataset.portfolioThumbOffset || '0', 10) || 0;

    const updateVisible = () => {
      current = Math.max(0, Math.min(current, mainSlides.length - 1));
      if (isCategoryStrip) {
        mainTrack.style.transform = 'none';
      } else {
        mainTrack.style.transform = `translate3d(-${current * 100}%, 0, 0)`;
      }

      mainSlides.forEach((slide, index) => {
        slide.classList.toggle('is-active', index === current);
      });

      thumbs.forEach((thumb, thumbIndex) => {
        thumb.classList.toggle('is-active', thumbIndex === current - thumbOffset);
      });

      pauseVideos();
    };

    PortfolioGallery.bindSliderTrackMedia(mainTrack, mainSlides, () => {
      requestAnimationFrame(updateVisible);
    });

    const goTo = (index, scrollThumb = false) => {
      current = Math.max(0, Math.min(index, mainSlides.length - 1));
      updateVisible();
      if (scrollThumb) scrollActiveThumb();
    };

    PortfolioGallery.bindMainCarouselSwipe(
      project,
      () => current,
      () => mainSlides.length,
      goTo
    );

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', (event) => {
        event.stopPropagation();
        const index = Number.parseInt(thumb.dataset.slideIndex, 10);
        if (!Number.isNaN(index)) goTo(index + thumbOffset, true);
      });
    });

    goTo(0);
    this.projectCarouselUpdates.push(updateVisible);
  }

  refreshVisibleItems() {
    this.visibleItems = PortfolioGallery.collectVisibleItems();
  }

  getLightboxItems() {
    return this.lightboxItems?.length ? this.lightboxItems : this.visibleItems;
  }

  openLightboxFromItem(item) {
    const controller = PortfolioGallery.getFilterController() || this;
    if (!item || !controller.lightbox || !controller.lightboxMedia) return;

    const gallery = item.closest('portfolio-gallery') || controller;
    const items = PortfolioGallery.collectAllGalleryMainItems(gallery);
    const activeItem =
      item.closest('[data-portfolio-slide]')?.querySelector('[data-portfolio-item].portfolio-gallery__item--main') ||
      item;

    controller.lightboxItems = items.length ? items : [activeItem];
    controller.currentIndex = Math.max(0, controller.lightboxItems.indexOf(activeItem));
    if (controller.currentIndex < 0) controller.currentIndex = 0;

    controller.renderLightboxMedia(controller.lightboxItems[controller.currentIndex]);
    controller.lightbox.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    controller.updateLightboxMeta(controller.lightboxItems[controller.currentIndex]);
    document.querySelectorAll('[data-portfolio-project-carousel]').forEach((carousel) => {
      carousel.dispatchEvent(new CustomEvent('portfolio:lightbox-open'));
    });
  }

  openLightbox(index) {
    const controller = PortfolioGallery.getFilterController() || this;
    const items = controller.getLightboxItems();
    const item = items[index];
    if (!item || !controller.lightbox || !controller.lightboxMedia) return;

    controller.lightboxItems = null;
    controller.currentIndex = index;
    controller.renderLightboxMedia(item);
    controller.lightbox.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    controller.updateLightboxMeta(item);
  }

  closeLightbox() {
    const controller = PortfolioGallery.getFilterController() || this;
    if (!controller.lightbox) return;
    controller.lightbox.classList.remove('is-open');
    document.body.style.overflow = '';
    controller.lightboxMedia.innerHTML = '';
    controller.lightboxItems = null;
    document.querySelectorAll('[data-portfolio-project-carousel]').forEach((carousel) => {
      carousel.dispatchEvent(new CustomEvent('portfolio:lightbox-close'));
    });
  }

  stepLightbox(direction) {
    const controller = PortfolioGallery.getFilterController() || this;
    const items = controller.getLightboxItems();
    if (!items.length) return;
    controller.currentIndex = (controller.currentIndex + direction + items.length) % items.length;
    const item = items[controller.currentIndex];
    controller.renderLightboxMedia(item);
    controller.updateLightboxMeta(item);
  }

  static bindLightboxZoom(mediaHost) {
    const zoomRoot = mediaHost.querySelector('[data-portfolio-lightbox-zoom]');
    const img = zoomRoot?.querySelector('img');
    if (!zoomRoot || !img) return;

    let scale = 1;
    let panX = 0;
    let panY = 0;
    let dragging = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let panStartX = 0;
    let panStartY = 0;

    const apply = () => {
      img.style.transform = `translate(${panX}px, ${panY}px) scale(${scale})`;
      zoomRoot.classList.toggle('is-zoomed', scale > 1.01);
    };

    zoomRoot.addEventListener('click', (event) => {
      if (dragging) return;
      if (event.target !== img) return;
      if (scale <= 1.01) {
        scale = 2;
      } else {
        scale = 1;
        panX = 0;
        panY = 0;
      }
      apply();
    });

    zoomRoot.addEventListener(
      'wheel',
      (event) => {
        event.preventDefault();
        const delta = event.deltaY > 0 ? -0.12 : 0.12;
        scale = Math.min(4, Math.max(1, scale + delta));
        if (scale <= 1.01) {
          scale = 1;
          panX = 0;
          panY = 0;
        }
        apply();
      },
      { passive: false }
    );

    zoomRoot.addEventListener('pointerdown', (event) => {
      if (scale <= 1.01 || event.target !== img) return;
      dragging = false;
      dragStartX = event.clientX;
      dragStartY = event.clientY;
      panStartX = panX;
      panStartY = panY;
      zoomRoot.classList.add('is-dragging');
      zoomRoot.setPointerCapture?.(event.pointerId);
    });

    zoomRoot.addEventListener('pointermove', (event) => {
      if (scale <= 1.01) return;
      const dx = event.clientX - dragStartX;
      const dy = event.clientY - dragStartY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragging = true;
      panX = panStartX + dx;
      panY = panStartY + dy;
      apply();
    });

    const endDrag = (event) => {
      zoomRoot.classList.remove('is-dragging');
      zoomRoot.releasePointerCapture?.(event.pointerId);
      window.setTimeout(() => {
        dragging = false;
      }, 0);
    };

    zoomRoot.addEventListener('pointerup', endDrag);
    zoomRoot.addEventListener('pointercancel', endDrag);
  }

  renderLightboxMedia(item) {
    const type = item.dataset.mediaType;
    const src = item.dataset.mediaSrc;
    const poster = item.dataset.mediaPoster;

    if (type === 'video' && src) {
      this.lightboxMedia.innerHTML = `
        <video class="portfolio-gallery__lightbox-video" controls autoplay playsinline poster="${poster || ''}">
          <source src="${src}" type="video/mp4">
        </video>
      `;
      return;
    }

    this.lightboxMedia.innerHTML = `
      <div class="portfolio-gallery__lightbox-zoom" data-portfolio-lightbox-zoom>
        <img class="portfolio-gallery__lightbox-image" src="${src}" alt="${item.dataset.title || ''}">
      </div>
    `;
    PortfolioGallery.bindLightboxZoom(this.lightboxMedia);
  }

  updateLightboxMeta(item) {
    const items = this.getLightboxItems();
    if (this.lightboxCaption) {
      const title = item.dataset.title || '';
      const caption = item.dataset.caption || '';
      this.lightboxCaption.innerHTML = title
        ? `<strong>${title}</strong>${caption ? `<span>${caption}</span>` : ''}`
        : caption;
    }

    if (this.lightboxCounter) {
      this.lightboxCounter.textContent = `${this.currentIndex + 1} / ${items.length}`;
    }
  }
}

if (!customElements.get('portfolio-gallery')) {
  customElements.define('portfolio-gallery', PortfolioGallery);
}
